import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { Calendar, Clock, MapPin, Users, TrendingUp, Activity } from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface Booking {
  id: string;
  booking_type: string;
  trainer_id: string | null;
  nutritionist_id: string | null;
  start_time: string;
  duration_minutes: number;
  status: string;
  total_amount: number;
  location_address: string | null;
  trainer_name?: string;
  nutritionist_name?: string;
}

export default function ClientDashboardPage() {
  const { profile } = useAuth();
  const [upcomingBookings, setUpcomingBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalBookings: 0,
    upcomingSessions: 0,
    totalSpent: 0,
  });

  const loadDashboardData = useCallback(async () => {
    if (!profile) return;

    try {
      const now = new Date().toISOString();

      // Fetch upcoming bookings
      const { data: bookings, error } = await supabase
        .from('home_bookings')
        .select('*')
        .eq('client_id', profile.id)
        .gte('start_time', now)
        .order('start_time', { ascending: true })
        .limit(5);

      if (error) throw error;

      // Fetch trainer and nutritionist names
      const enrichedBookings = await Promise.all(
        (bookings || []).map(async (booking) => {
          if (booking.trainer_id) {
            const { data: trainer } = await supabase
              .from('trainers')
              .select('name')
              .eq('id', booking.trainer_id)
              .single();
            return { ...booking, trainer_name: trainer?.name };
          }
          if (booking.nutritionist_id) {
            const { data: nutritionist } = await supabase
              .from('nutritionists')
              .select('name')
              .eq('id', booking.nutritionist_id)
              .single();
            return { ...booking, nutritionist_name: nutritionist?.name };
          }
          return booking;
        })
      );

      setUpcomingBookings(enrichedBookings as Booking[]);

      // Fetch statistics
      const { data: allBookings } = await supabase
        .from('home_bookings')
        .select('status, total_amount, start_time')
        .eq('client_id', profile.id);

      const totalBookings = allBookings?.length || 0;
      const upcomingSessions = allBookings?.filter(
        (b) => new Date(b.start_time) > new Date() && b.status !== 'cancelled'
      ).length || 0;
      const totalSpent =
        allBookings?.reduce((sum, b) => sum + (b.total_amount || 0), 0) || 0;

      setStats({ totalBookings, upcomingSessions, totalSpent });
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, [profile]);

  useEffect(() => {
    if (profile) {
      loadDashboardData();
    }
  }, [profile, loadDashboardData]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <>
        <Header />
        <div className="flex min-h-screen items-center justify-center">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
            <p className="mt-4 text-sm text-gray-600">Loading...</p>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <main className="min-h-screen bg-gray-50 py-8">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">
              Welcome back, {profile?.full_name || 'User'}!
            </h1>
            <p className="mt-2 text-gray-600">
              Here's an overview of your fitness journey
            </p>
          </div>

          {/* Stats Grid */}
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Total Bookings</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <Activity className="h-6 w-6 text-blue-600" />
                {stats.totalBookings}
              </p>
            </div>
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Upcoming Sessions</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <Calendar className="h-6 w-6 text-green-600" />
                {stats.upcomingSessions}
              </p>
            </div>
            <div className="rounded-3xl border border-blue-100 bg-white/90 p-5 shadow-sm">
              <p className="text-sm font-semibold text-gray-700">Total Spent</p>
              <p className="text-3xl font-bold mt-2 flex items-center gap-2">
                <TrendingUp className="h-6 w-6 text-purple-600" />
                ${stats.totalSpent.toFixed(2)}
              </p>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mb-8 grid gap-4 sm:grid-cols-3">
            <Link
              to="/trainer-discovery"
              className="rounded-xl border border-blue-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-blue-100 p-3">
                  <Users className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Book a Trainer</h3>
                  <p className="text-sm text-gray-600">Find personal trainers</p>
                </div>
              </div>
            </Link>
            <Link
              to="/nutritionist-discovery"
              className="rounded-xl border border-green-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-green-100 p-3">
                  <Activity className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Book a Nutritionist</h3>
                  <p className="text-sm text-gray-600">Get nutrition advice</p>
                </div>
              </div>
            </Link>
            <Link
              to="/park-classes"
              className="rounded-xl border border-purple-200 bg-white p-6 shadow-sm transition hover:shadow-md"
            >
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-purple-100 p-3">
                  <MapPin className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Park Classes</h3>
                  <p className="text-sm text-gray-600">Join outdoor sessions</p>
                </div>
              </div>
            </Link>
          </div>

          {/* Upcoming Bookings */}
          <section className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-gray-900">Upcoming Bookings</h2>
              <Link
                to="/bookings"
                className="text-sm font-semibold text-blue-600 hover:text-blue-700"
              >
                View all
              </Link>
            </div>
            {upcomingBookings.length === 0 ? (
              <div className="rounded-xl border border-gray-200 bg-white p-12 text-center">
                <Calendar className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-4 text-lg font-semibold text-gray-900">
                  No upcoming bookings
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  Book your first session to get started!
                </p>
                <div className="mt-6 flex justify-center gap-4">
                  <Link
                    to="/trainer-discovery"
                    className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
                  >
                    Book a Trainer
                  </Link>
                  <Link
                    to="/nutritionist-discovery"
                    className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Book a Nutritionist
                  </Link>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {upcomingBookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {booking.trainer_name || booking.nutritionist_name || 'Session'}
                          </h3>
                          <span
                            className={`rounded-full px-2 py-1 text-xs font-semibold ${
                              booking.status === 'confirmed'
                                ? 'bg-green-100 text-green-700'
                                : booking.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}
                          >
                            {booking.status}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-gray-600">
                          {booking.booking_type === 'trainer'
                            ? 'Personal Training Session'
                            : 'Nutrition Consultation'}
                        </p>
                        <div className="mt-4 flex flex-wrap gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {formatDate(booking.start_time)}
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {formatTime(booking.start_time)} ({booking.duration_minutes} min)
                          </div>
                          {booking.location_address && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              {booking.location_address}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ml-4 text-right">
                        <p className="text-lg font-bold text-gray-900">
                          ${booking.total_amount?.toFixed(2) || '0.00'}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}
